import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanySettings,
  CompanySettingsOrganizationPage,
  CompanySettingsRolesPage,
  CompanySettingsCompanyPoliciesPage,
  CompanySettingsTemplatesPage,
  CompanySettingsCalendarsPage,
  DocumentTemplateBuilder,
  DocumentTemplateSigning,
} from "@/utils/lazyLoadComponents";
import { TEMPLATES_ID_KEY } from "@/pages/(home)/(company)/settings/templates/_data/paths";
// Redirect targets come from `module-settings.paths.ts`, NOT from the modules'
// own route files. Importing those here would pull their eagerly-imported page
// components in during route-config evaluation, which reorders module init and
// detonates latent import cycles (it broke
// `routes.offboarding.ts` ⇄ `payrolls/final-settlements/detail` with a TDZ
// ReferenceError). That file imports nothing but `route.utils`.
import {
  DYNAMIC_TEAM_ROLE_ID_KEY,
  MODULE_SETTINGS_PATHS as MS,
} from "./module-settings.paths";

export const SETTINGS_ID_KEY = "id";

// ─── Path Segments ────────────────────────────────────────────────────────────
export enum COMPANY_SETTINGS_PATH_SEGMENTS {
  ROOT = "settings",
  ORGANIZATION = "organization-settings",
  ROLES = "roles",
  COMPANY_POLICIES = "company-policies",
  TEMPLATES = "document-templates",
  CALENDARS = "calendars",
  CREATE = "create",
  DETAILS = `:${SETTINGS_ID_KEY}`,
  EDIT = "edit",
}

// ─── Legacy redirects ─────────────────────────────────────────────────────────
// Settings that moved out of this page and into their owning module keep their
// old URLs alive here for at least one release, `?vTab=` included, so bookmarks
// and any stale links keep resolving.
// Declared before `companySettingsRoutes` because it is spread into it — a const
// referenced during that object's initialization must already be initialized.
const LEGACY_MOVED_SETTINGS_REDIRECTS: RouteObject[] = [
  // Team Settings → Teams module
  {
    path: "team-settings",
    loader: ({ request }) =>
      redirect(`${MS.TEAMS.ROOT}${new URL(request.url).search}`),
  },
  // The team-role upsert screens used to sit at their own top-level
  // `/company/settings/team-roles-settings` route (registered outside this
  // file); they are now children of the Teams settings page.
  {
    path: "team-roles-settings",
    loader: () => redirect(MS.TEAMS.TEAM_ROLES),
  },
  {
    path: "team-roles-settings/create",
    loader: () => redirect(MS.TEAMS.TEAM_ROLE_CREATE),
  },
  {
    path: `team-roles-settings/:${DYNAMIC_TEAM_ROLE_ID_KEY}`,
    loader: ({ params }) =>
      redirect(
        MS.TEAMS.TEAM_ROLE_VIEW(
          params[DYNAMIC_TEAM_ROLE_ID_KEY]!,
        ),
      ),
  },
  {
    path: `team-roles-settings/:${DYNAMIC_TEAM_ROLE_ID_KEY}/edit`,
    loader: ({ params }) =>
      redirect(
        MS.TEAMS.TEAM_ROLE_EDIT(
          params[DYNAMIC_TEAM_ROLE_ID_KEY]!,
        ),
      ),
  },

  // Straight tab → module-settings moves. `?vTab=` is forwarded untouched
  // because every vTab value was carried over unchanged.
  ...(
    [
      ["leaves", MS.LEAVE_MANAGEMENT.ROOT],
      ["time-attendance", MS.TIME_ATTENDANCE.ROOT],
      ["payroll-settings", MS.PAYROLL.ROOT],
      ["compensation", MS.PAYROLL.ROOT],
      ["inventory-and-assets", MS.ASSETS.ROOT],
      ["expenses", MS.EXPENSES.ROOT],
      ["documents", MS.DOCUMENTS.ROOT],
      ["performance-settings", MS.PERFORMANCE.ROOT],
      ["onboarding-settings", MS.ONBOARDING.ROOT],
      ["digital-id", MS.DIGITAL_ID.ROOT],
    ] as const
  ).map(([from, to]) => ({
    path: from,
    loader: ({ request }: { request: Request }) =>
      redirect(`${to}${new URL(request.url).search}`),
  })),

  // Shift Settings had no vTab of its own at the hTab level; its inner
  // `?vTab=shifts` is now a vTab of the Time & Attendance settings page.
  {
    path: "shift",
    loader: () => redirect(MS.TIME_ATTENDANCE.VTAB_SHIFTS),
  },
  // The standalone Overtime Policy route rendered the same component as the
  // Time Attendance `overtime-policy` vTab — that duplicate is gone.
  {
    path: "overtime-policy",
    loader: () => redirect(MS.TIME_ATTENDANCE.VTAB_OVERTIME),
  },
  // Performance metric form screens.
  {
    path: "performance-settings/create",
    loader: () => redirect(MS.PERFORMANCE.METRICS_CREATE),
  },
  {
    path: `performance-settings/:${SETTINGS_ID_KEY}/edit`,
    loader: ({ params }) =>
      redirect(
        MS.PERFORMANCE.METRICS_EDIT(
          params[SETTINGS_ID_KEY]!,
        ),
      ),
  },
];

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companySettingsRoutes: RouteObject = {
  path: COMPANY_SETTINGS_PATH_SEGMENTS.ROOT,
  // `settingsSpace`: the app Layout swaps its main menu for the settings
  // sidebar on every route under this one (see useSettingsRouteNav).
  handle: { breadcrumb: "Settings", settingsSpace: "company" },
  HydrateFallback: Spinner,
  children: [
    // ── Settings page frame (SettingsLayout), inside the app Layout, which
    // puts the settings sidebar in its main menu's slot. Each section is a
    // path segment; its sub-sections stay a query param (?vTab=...) and are
    // listed in the settings sidebar (config/settings-nav.config.ts). ───────
    {
      Component: CompanySettings,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_SETTINGS_PATHS.TAB_ORGANIZATION),
        },
        {
          path: COMPANY_SETTINGS_PATH_SEGMENTS.ORGANIZATION,
          handle: { breadcrumb: "Organization Settings" },
          Component: CompanySettingsOrganizationPage,
        },
        {
          path: COMPANY_SETTINGS_PATH_SEGMENTS.ROLES,
          handle: { breadcrumb: "Roles & Permissions" },
          Component: CompanySettingsRolesPage,
        },
        {
          path: COMPANY_SETTINGS_PATH_SEGMENTS.COMPANY_POLICIES,
          handle: { breadcrumb: "Company Policies" },
          Component: CompanySettingsCompanyPoliciesPage,
        },
        {
          path: COMPANY_SETTINGS_PATH_SEGMENTS.TEMPLATES,
          handle: { breadcrumb: "Templates" },
          Component: CompanySettingsTemplatesPage,
        },
        {
          path: COMPANY_SETTINGS_PATH_SEGMENTS.CALENDARS,
          handle: { breadcrumb: "Calendars" },
          Component: CompanySettingsCalendarsPage,
        },
      ],
    },

    ...LEGACY_MOVED_SETTINGS_REDIRECTS,
  ],
};

// ─── Route Definition (outside <Layout/>) ────────────────────────────────────
// The template builder is registered under <BuilderLayout/> in the root router
// (keeps the app top bar, hides the sidebar) — same as the workflow builder.
export const companyTemplateBuilderRoutes: RouteObject[] = [
  {
    path: withCompanyPrefix(
      `${COMPANY_SETTINGS_PATH_SEGMENTS.ROOT}/templates/:${TEMPLATES_ID_KEY}`,
    ),
    Component: DocumentTemplateBuilder,
  },
];

// The signing canvas stays fully full-screen (its own chrome, no app nav).
export const companyTemplateSigningRoutes: RouteObject[] = [
  {
    path: withCompanyPrefix(
      `${COMPANY_SETTINGS_PATH_SEGMENTS.ROOT}/templates/:${TEMPLATES_ID_KEY}/sign`,
    ),
    Component: DocumentTemplateSigning,
  },
];

// ─── Resolved Paths ───────────────────────────────────────────────────────────
const _SETTINGS_ROOT = withCompanyPrefix(addSlashes(COMPANY_SETTINGS_PATH_SEGMENTS.ROOT));
// hTab is now a real path segment; vTab stays a query param scoped to that page.
const _tab  = (hTab: string)               => `${_SETTINGS_ROOT}/${hTab}`;
const _vTab = (hTab: string, vTab: string) => `${_SETTINGS_ROOT}/${hTab}?vTab=${vTab}`;

export const COMPANY_SETTINGS_PATHS = Object.freeze({
  ROOT: _SETTINGS_ROOT,

  // ── hTab tab routes (Settings index page) ────────────────────────────────
  TAB_ORGANIZATION:    _tab("organization-settings"),
  TAB_ROLES:           _tab("roles"),
  TAB_COMPANY_POLICIES:_tab("company-policies"),
  TAB_TEMPLATES:       _tab("document-templates"),
  TAB_CALENDARS:       _tab("calendars"),

  // ── vTab deep-links (secondary sidebar within each hTab) ─────────────────

  // Organization Settings
  VTAB_ORG_COMPANY_INFO:  _vTab("organization-settings", "company-information"),
  VTAB_ORG_BRANCHES:      _vTab("organization-settings", "branches-or-offices"),
  VTAB_ORG_DEPARTMENTS:   _vTab("organization-settings", "departments-or-divisions"),
  VTAB_ORG_POSITIONS:     _vTab("organization-settings", "positions-designations"),
  VTAB_ORG_STRUCTURE:     _vTab("organization-settings", "organizational-structure"),

  // Roles & Permissions
  VTAB_ROLES_LIST:       _vTab("roles", "roles-and-permissions"),
  VTAB_ROLES_USER_ROLES: _vTab("roles", "user-roles"),

  // Leave Settings moved into the Leave Management module — see
  // COMPANY_LEAVES_MANAGEMENT_PATHS.SETTINGS / SETTINGS_VTAB_*.

  // Time Attendance, Shift, Compensation, Payroll, Inventory & Assets,
  // Expenses and Documents settings all moved into their owning modules — see
  // each module's COMPANY_*_PATHS.SETTINGS / SETTINGS_VTAB_* members.

  // Company Policies
  VTAB_POLICIES_CATEGORIES: _vTab("company-policies", "categories"),
  VTAB_POLICIES_LIST:       _vTab("company-policies", "policies"),

  // Team, Performance and Onboarding settings moved into their owning modules
  // — see each module's COMPANY_*_PATHS.SETTINGS / SETTINGS_VTAB_* members.

  // Calendars
  VTAB_CAL_TYPES:    _vTab("calendars", "calendar-types"),
  VTAB_CAL_HOLIDAYS: _vTab("calendars", "holiday-calendar"),

});
